import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import SearchInput from "@atoms/SearchInput/SearchInput"
import {
  useSiteAndUnitTaxonomies,
  PUBLIC_TRANSPORT_DATA,
  PARKING_DATA,
} from "@context/search/queries"
import { useSearchResultContext } from "@context/searchResult"
import {
  SEARCH_TERM_CHANGE,
  PRICE_RANGE_CHANGE,
  CHECKBOX_GROUP_CHANGE,
} from "@context/searchResult/actions"
import DesktopCheckboxFilter from "@organisms/DesktopCheckboxFilter/DesktopCheckboxFilter"
import FiltersModal from "@organisms/FiltersModal/FiltersModal"
import MobileFilters from "@organisms/MobileFilters/MobileFilters"
import ResetFiltersButton from "@atoms/ResetFiltersButton/ResetFiltersButton"
import useScreenSize from "@atoms/UseScreenSize/UseScreenSize"

import { useSearchContext } from "@context/search"

const SearchResultFilters = ({ onSave, onClear }) => {
  const width = useScreenSize().width
  const [isSSR, setIsSSR] = useState(true)
  const BREAKPOINT = 1100

  const { t } = useTranslation()
  const {
    state: { area },
  } = useSearchContext()

  useEffect(() => {
    setIsSSR(false)
  }, [])

  const {
    dispatch,
    state: {
      searchTerm,
      siteDistricts,
      siteTypes,
      siteServices,
      sitePublicTransports,
      siteParking,
      unitAttributes,
      unitTechnicalAttributes,
    },
  } = useSearchResultContext()
  const {
    allWpTaxonomySiteArea: { nodes: areas },
    allWpTaxonomySiteType: { nodes: types },
    allWpTaxonomySiteService: { nodes: services },
    allWpTaxonomyUnitAttribute: { nodes: attributes },
    allWpTaxonomyUnitTechnicalAttribute: { nodes: technicalAttributes },
  } = useSiteAndUnitTaxonomies()

  const districts = areas.find((a) => a.name === area?.name)?.wpChildren?.nodes

  // This is needed so I can avoid showing mobile filters while on desktop and then having a "blink" which shows the correct ones
  // Ref to https://stephencook.dev/blog/using-window-in-react-ssr/
  if (isSSR) return null

  return (
    <div className="mx-mobile-gutter lg:mx-0 py-4">
      {width >= BREAKPOINT ? (
        <div className="flex flex-wrap justify-start gap-4">
          <SearchInput
            placeholder={t("Eg. Site name / address")}
            widthClass="w-98"
            value={searchTerm}
            onChange={({ target }) =>
              dispatch({ type: SEARCH_TERM_CHANGE, payload: target.value })
            }
            onSearch={onSave}
          />
          <DesktopCheckboxFilter
            label={t("District")}
            list={districts}
            onCheck={(value) =>
              dispatch({ type: CHECKBOX_GROUP_CHANGE, payload: value })
            }
            onSave={onSave}
            initialState={siteDistricts}
            groupName="siteDistricts"
            textSearchSettings={{
              apply: true,
              placeholder: t("Type in district"),
            }}
          />
          <DesktopCheckboxFilter
            label={t("Site type")}
            list={types}
            onCheck={(value) =>
              dispatch({ type: CHECKBOX_GROUP_CHANGE, payload: value })
            }
            onSave={onSave}
            initialState={siteTypes}
            groupName="siteTypes"
          />
          <DesktopCheckboxFilter
            label={t("Site services")}
            list={services}
            onCheck={(value) =>
              dispatch({ type: CHECKBOX_GROUP_CHANGE, payload: value })
            }
            onSave={onSave}
            initialState={siteServices}
            groupName="siteServices"
          />
          <DesktopCheckboxFilter
            label={t("Public transport")}
            list={PUBLIC_TRANSPORT_DATA()}
            onCheck={(value) =>
              dispatch({ type: CHECKBOX_GROUP_CHANGE, payload: value })
            }
            onSave={onSave}
            initialState={sitePublicTransports}
            groupName="sitePublicTransports"
          />
          <FiltersModal
            label={t("More filters")}
            onSave={onSave}
            onCheck={(value) =>
              dispatch({ type: CHECKBOX_GROUP_CHANGE, payload: value })
            }
            filters={[
              {
                heading: t("Unit features"),
                list: attributes,
                className: "grid grid-cols-2 mb-5",
                groupName: "unitAttributes",
                initialState: unitAttributes,
              },
              {
                heading: t("Unit technical features"),
                list: technicalAttributes,
                className: "grid grid-cols-2 mb-5",
                groupName: "unitTechnicalAttributes",
                initialState: unitTechnicalAttributes,
              },
              {
                heading: t("Parking spaces"),
                list: PARKING_DATA(),
                className: "mb-5",
                groupName: "siteParking",
                initialState: siteParking,
              },
            ]}
          />
          <ResetFiltersButton onClick={() => onClear()} />
        </div>
      ) : (
        <MobileFilters
          label={t("Filter results")}
          rangeLabel={t("Rent / month")}
          filters={[
            {
              heading: t("District"),
              list: districts,
              initialState: siteDistricts,
              groupName: "siteDistricts",
              textSearchSettings: {
                apply: true,
                placeholder: t("Type in district"),
              },
            },
            {
              heading: t("Site type"),
              list: types,
              initialState: siteTypes,
              groupName: "siteTypes",
            },
            {
              heading: t("Site services"),
              list: services,
              initialState: siteServices,
              groupName: "siteServices",
            },
            {
              heading: t("Public transport"),
              list: PUBLIC_TRANSPORT_DATA(),
              initialState: sitePublicTransports,
              groupName: "sitePublicTransports",
            },
            {
              heading: t("Unit features"),
              list: attributes,
              groupName: "unitAttributes",
              initialState: unitAttributes,
            },
            {
              heading: t("Unit technical features"),
              list: technicalAttributes,
              groupName: "unitTechnicalAttributes",
              initialState: unitTechnicalAttributes,
            },
            {
              heading: t("Parking spaces"),
              list: PARKING_DATA(),
              groupName: "siteParking",
              initialState: siteParking,
            },
          ]}
          onCheck={(value) =>
            dispatch({ type: CHECKBOX_GROUP_CHANGE, payload: value })
          }
          onSearchChange={({ target }) =>
            dispatch({ type: SEARCH_TERM_CHANGE, payload: target.value })
          }
          onRangeChange={(range) =>
            dispatch({ type: PRICE_RANGE_CHANGE, payload: range })
          }
          onSave={onSave}
        />
      )}
    </div>
  )
}

SearchResultFilters.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
}

export default SearchResultFilters
